import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import MultichoixSteps from './components/MultichoixSteps';
import TreatmentResults from './components/TreatmentResults';
import MultichoixErrorBoundary from './components/multichoix/MultichoixErrorBoundary';
import { useMultichoixURLState, MULTICHOIX_VIEWS } from './hooks/useMultichoixURLState';
import { useMultichoixEnrichedData } from './hooks/useMultichoixEnrichedData';

const updateBreadcrumb = (isResultsView) => {
  const dynamicBreadcrumb = document.getElementById('dynamic-breadcrumb');
  const dynamicContainer = document.getElementById('dynamic-breadcrumb-container');

  if (dynamicBreadcrumb && dynamicContainer) {
    if (isResultsView) {
      dynamicBreadcrumb.textContent = 'Résultats de recherche';
      dynamicContainer.classList.remove('hidden');
      dynamicContainer.classList.add('flex');
    } else {
      dynamicContainer.classList.add('hidden');
      dynamicContainer.classList.remove('flex');
    }
  }
};

const TreatmentMultichoixSearch = () => {
  const { t } = useTranslation();
  const [searchData, setSearchData] = useState(null);

  // Use the new URL state hook (stores only IDs)
  const {
    urlState,
    currentView,
    selections,
    updateField,
    goToResults,
    goToSteps,
    hasValidSearchParams,
  } = useMultichoixURLState();

  // Callback to sync selection changes to URL
  const handleSelectionChange = useCallback(
    (field, values) => {
      updateField(field, values);
    },
    [updateField]
  );

  // Use the enriched data hook (fetches labels for cold start)
  const {
    enrichedData,
    isLoadingLabels,
    buildSelectionLabels,
    updateEnrichedField,
  } = useMultichoixEnrichedData(urlState);

  // Handle search submission from MultichoixSteps
  const handleSearch = useCallback(
    (data) => {
      // Merge labels: use enriched data (from bulk-initialize) as base,
      // override with step management labels where available.
      // This ensures labels are present whether coming from cold start or interactive use.
      const enrichedLabels = buildSelectionLabels();
      const mergedLabels = { ...enrichedLabels };
      if (data.selectionLabels) {
        Object.entries(data.selectionLabels).forEach(([field, labels]) => {
          if (labels && labels.length > 0) {
            mergedLabels[field] = labels;
          }
        });
      }

      const searchDataWithLabels = {
        selections: data.selections,
        selectionLabels: mergedLabels,
        completedSteps: data.completedSteps,
      };
      setSearchData(searchDataWithLabels);

      // Also update enriched data with step labels for cold-start recovery on refresh
      if (data.selectionLabels) {
        Object.entries(data.selectionLabels).forEach(([field, labels]) => {
          if (labels && labels.length > 0) {
            updateEnrichedField(field, labels);
          }
        });
      }

      goToResults(data.selections);
    },
    [goToResults, buildSelectionLabels, updateEnrichedField]
  );

  // Handle back to steps navigation
  const handleBackToSteps = useCallback(() => {
    goToSteps();
  }, [goToSteps]);

  // Cold start effect: reconstruct searchData from URL + enriched labels when in results view
  useEffect(() => {
    const isResultsView = currentView === MULTICHOIX_VIEWS.RESULTS;
    const hasData = Boolean(searchData);

    if (isResultsView && !hasData && !isLoadingLabels) {
      if (hasValidSearchParams()) {
        const selectionLabels = buildSelectionLabels();

        // Check if labels are actually populated before setting searchData
        // This prevents setting searchData with empty labels while API is still fetching
        const hasLabels = Object.keys(selectionLabels).length > 0;
        const hasSelections = Object.keys(selections).some(
          (key) => selections[key]?.length > 0
        );

        // Only set searchData if we have labels, or if there are no selections (nothing to fetch)
        if (hasLabels || !hasSelections) {
          const completedSteps = Object.keys(selections).filter(
            (key) => selections[key]?.length > 0
          ).length;
          setSearchData({
            selections,
            selectionLabels,
            completedSteps,
          });
        }
        // If hasSelections but !hasLabels, wait for labels to be fetched
      } else {
        goToSteps();
      }
    }
  }, [
    currentView,
    searchData,
    isLoadingLabels,
    hasValidSearchParams,
    selections,
    buildSelectionLabels,
    goToSteps,
  ]);

  // Update breadcrumb when view changes
  useEffect(() => {
    updateBreadcrumb(currentView === MULTICHOIX_VIEWS.RESULTS);
  }, [currentView]);

  // Build initialSearchData from URL + enriched data for MultichoixSteps
  const initialSearchData = useMemo(() => {
    const hasSelections = Object.keys(selections).some(
      (key) => selections[key]?.length > 0
    );

    if (!hasSelections) {
      return null;
    }

    return {
      selections,
      selectionLabels: buildSelectionLabels(),
      completedSteps: Object.keys(selections).filter(
        (key) => selections[key]?.length > 0
      ).length,
    };
  }, [selections, buildSelectionLabels]);

  return (
    <MultichoixErrorBoundary onReset={handleBackToSteps}>
      <Spin
        spinning={isLoadingLabels}
        tip={t('loading_data', 'Chargement des données...')}
        size="large"
      >
        {currentView === MULTICHOIX_VIEWS.RESULTS && searchData ? (
          <TreatmentResults searchData={searchData} onBackToSteps={handleBackToSteps} />
        ) : (
          <MultichoixSteps
            onSearch={handleSearch}
            onSelectionChange={handleSelectionChange}
            initialSearchData={initialSearchData}
            enrichedData={enrichedData}
          />
        )}
      </Spin>
    </MultichoixErrorBoundary>
  );
};

export default TreatmentMultichoixSearch;
